import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Loader2, Calendar, User } from "lucide-react";
import { format, differenceInYears } from "date-fns";
import { sv } from "date-fns/locale";
import JuniorBadge from "./JuniorBadge";
import { isJunior, getAge } from "@/lib/juniorUtils";

export default function ParentChildrenSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [childName, setChildName] = useState("");
  const [childBirthDate, setChildBirthDate] = useState("");
  const [childEmail, setChildEmail] = useState("");
  const [childPassword, setChildPassword] = useState("");

  // Fetch linked children
  const { data: links = [] } = useQuery({
    queryKey: ["parent-children", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("parent_child_links" as any)
        .select("*")
        .eq("parent_id", user!.id)
        .eq("status", "active");
      if (error) throw error;
      return (data as Record<string, unknown>[]) ?? [];
    },
    enabled: !!user?.id,
  });

  // Fetch child profiles
  const childIds = links.map((l: Record<string, unknown>) => l.child_id);
  const { data: childProfiles = [] } = useQuery({
    queryKey: ["child-profiles", childIds],
    queryFn: async () => {
      if (childIds.length === 0) return [];
      // KNOWN GAP: Sprint 1 Phase 2 (PR #214 / a0ce022) closed cross-user reads
      // on public.profiles for non-admin contexts. The junior feature reads
      // child profiles here as parent — child is a separate auth.users row,
      // neither "Profile read own row" nor "Admin all profiles" RLS policy
      // applies, so this returns 0 rows. Pre-existing fix needed before the
      // junior feature ships to production. Currently zero impact: PROD has
      // 0 rows in parent_child_links. Spawned follow-up task ships an
      // Option-A simple RLS policy `Parent read accepted child profile`.
      // FOLLOW-UP: also narrow SELECT * to explicit allow-list.
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .in("id", childIds);
      return data ?? [];
    },
    enabled: childIds.length > 0,
  });

  // Create child account
  const createChildMutation = useMutation({
    mutationFn: async () => {
      if (!user || !childName || !childBirthDate || !childEmail || !childPassword) {
        throw new Error("Fyll i alla fält");
      }

      // Sign up the child (the parent creates the account)
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: childEmail,
        password: childPassword,
        options: {
          data: { full_name: childName },
        },
      });
      if (signUpError) throw signUpError;
      if (!signUpData.user) throw new Error("Kunde inte skapa konto");

      const childId = signUpData.user.id;

      // Update the child's birth_date
      await supabase
        .from("profiles")
        .update({ birth_date: childBirthDate } as Record<string, unknown>)
        .eq("id", childId);

      // Create parent-child link
      await supabase
        .from("parent_child_links" as any)
        .insert({
          parent_id: user.id,
          child_id: childId,
        });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["parent-children"] });
      qc.invalidateQueries({ queryKey: ["child-profiles"] });
      setShowAdd(false);
      setChildName("");
      setChildBirthDate("");
      setChildEmail("");
      setChildPassword("");
      toast.success(t("payments:toast.childAccountCreated"));
    },
    onError: (e: Error) => toast.error(e.message || "Kunde inte skapa konto"),
  });

  if (links.length === 0 && !showAdd) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-5 w-5" />
              <span className="text-sm">Inga barn kopplade</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4 mr-1" /> Lägg till barn
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <Users className="h-4 w-4" /> Mina barn
        </h3>
        <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" /> Lägg till
        </Button>
      </div>

      <div className="grid gap-2">
        {childProfiles.map((child: Record<string, unknown>) => {
          const age = getAge(child.birth_date);
          return (
            <Card key={child.id}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{child.full_name || "Okänd"}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {age !== null && <span>{age} år</span>}
                      {child.birth_date && (
                        <span>{format(new Date(child.birth_date), "d MMM yyyy", { locale: sv })}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isJunior(child.birth_date) && <JuniorBadge />}
                  {child.level && (
                    <span className="text-xs text-muted-foreground">Nivå {child.level}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add child dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lägg till barn</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Barnets namn</Label>
              <Input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Förnamn Efternamn" />
            </div>
            <div className="space-y-1">
              <Label>Födelsedatum</Label>
              <Input type="date" value={childBirthDate} onChange={(e) => setChildBirthDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>E-post (barnets konto)</Label>
              <Input type="email" value={childEmail} onChange={(e) => setChildEmail(e.target.value)} placeholder="barn@email.se" />
            </div>
            <div className="space-y-1">
              <Label>Lösenord</Label>
              <Input type="password" value={childPassword} onChange={(e) => setChildPassword(e.target.value)} placeholder="Minst 6 tecken" minLength={6} />
            </div>
            {childBirthDate && !isJunior(childBirthDate) && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Obs: Personen är 18 år eller äldre och klassas inte som junior.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => createChildMutation.mutate()}
              disabled={!childName || !childBirthDate || !childEmail || !childPassword || createChildMutation.isPending}
            >
              {createChildMutation.isPending ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Skapa barnkonto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
